import axios from "axios";
import { Link } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useLocation } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Polygon, TileLayer, WMSTileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import {
  faArrowLeft,
  faEye,
  faImage,
} from "@fortawesome/free-solid-svg-icons";

const containerStyle = {
  width: "100%",
  height: "50vh",
};

const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

// Bhuvan WMS Layer URL
const bhuvanLayerUrl = 'https://bhuvan-ras1.nrsc.gov.in/tilecache/tilecache.py';

// Helper component to fit bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);

  return null;
};

// Helper function to parse coordinate pairs
const parseCoordinatePair = (pair) => {
  if (!Array.isArray(pair) || pair.length < 2) {
    return null;
  }

  const first = Number(pair[0]);
  const second = Number(pair[1]);

  if (Number.isNaN(first) || Number.isNaN(second)) {
    return null;
  }

  // Check if coordinates need to be swapped (lat/lng format)
  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return [second, first];
  }

  return [first, second];
};

// Helper function to normalize polygon shape data
const normalizePolygonShape = (rawPolygonShape) => {
  if (!rawPolygonShape) {
    return [];
  }

  let parsed = rawPolygonShape;
  if (typeof rawPolygonShape === "string") {
    try {
      parsed = JSON.parse(rawPolygonShape);
    } catch (_) {
      return [];
    }
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [];
  }

  // Handle array of arrays format
  if (Array.isArray(parsed[0])) {
    return parsed.map(parseCoordinatePair).filter(Boolean);
  }

  // Handle array of objects format
  return parsed
    .map((point) => {
      if (!point || typeof point !== "object") {
        return null;
      }

      const latValue = Number(
        point.lat ?? point.latitude ?? point.Latitude ?? point.y
      );
      const lngValue = Number(
        point.lng ?? point.lon ?? point.longitude ?? point.Longitude ?? point.x
      );

      if (Number.isNaN(latValue) || Number.isNaN(lngValue)) {
        return null;
      }

      return [latValue, lngValue];
    })
    .filter(Boolean);
};

// Custom WMSTileLayer component for Bhuvan
const BhuvanWMSTileLayer = ({ url, layers, ...props }) => {
  const map = useMap();
  
  useEffect(() => {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layers,
      format: 'image/png',
      transparent: true,
      attribution: 'Bhuvan NRSC',
      maxZoom: 18,
      version: '1.1.1',
      srs: 'EPSG:3857',
      Maxresolution: 156543.033928,
      styles: '',
      ...props
    }).addTo(map);

    return () => {
      map.removeLayer(wmsLayer);
    };
  }, [map, url, layers, props]);

  return null;
};

const PDMCDeatilPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location.state; // initial data passed via router
  const [farmerDetails, setFarmerDetails] = useState(initialState || null);
  const [cropSurveyData, setCropSurveyData] = useState(null);
  const [componentName, setComponentName] = useState("");
  const [activeImage, setActiveImage] = useState(null);
  const [coconutName, setCoconutName] = useState("");

  // Flag to ensure fetch is only done once.
  const [fetched, setFetched] = useState(false);

  // Determine schemeCodeModel from sessionStorage
  const schemeCodeModel = sessionStorage.getItem("schemeCodeModel");

  // When the component mounts (or farmerDetails changes), fetch detailed data if needed
  useEffect(() => {
    if (farmerDetails && !fetched) {
      console.log("Initial farmer details:", farmerDetails.farmerCode);
      // For schemeCodeModel "14" or if it's not set, fetch NMEOOP details only once
      if (schemeCodeModel === "1" || !schemeCodeModel) {
        fetchDetailedDataPDMC(farmerDetails.farmerCode.farmerCode);
        setFetched(true);
      }
    }
  }, [farmerDetails, schemeCodeModel, fetched]);

  console.log(sessionStorage.getItem("schemeCodeModel"));

  async function fetchDetailedDataPDMC(value) {
    try {
      const result = await axios.post(
        `${
          process.env.NODE_ENV === "development"
            ? process.env.REACT_APP_DEV_MODE
            : process.env.REACT_APP_PRO_MODE
        }/api/Nature/FarmerDetails/getFarmerDetailsList`,
        {
          schemeCode: 1,
          orgTypeCode: 0,
          SurveyUserCode: "0",
          stateCode: "0",
          districtCode: "0",
          subDistrictCode: "0",
          farmerCode: value,
        }
      );
      const data = result.data.dataContent[0];
      console.log("datadatadata", data);
      // Update our local state with the fetched details
      setFarmerDetails(data);
      setCropSurveyData(data);
    } catch (error) {
      console.error("Error fetching NMEOOP details:", error);
    }
  }

  // Process polygon paths for Leaflet
  const polygonPaths = useMemo(() => {
    const polygonData = cropSurveyData?.farmerCropSurveyPolygonList || 
                        farmerDetails?.farmerCropSurveyPolygonList || [];
    
    if (!Array.isArray(polygonData) || polygonData.length === 0) {
      return [];
    }

    const shapes = polygonData
      .map((item) => normalizePolygonShape(item?.polygonShape))
      .filter((shape) => shape.length > 0);

    return shapes;
  }, [cropSurveyData, farmerDetails]);

  // Calculate map bounds from polygon paths
  const mapBounds = useMemo(() => {
    if (polygonPaths.length === 0) {
      return null;
    }

    const allPoints = polygonPaths.flat();
    if (allPoints.length === 0) {
      return null;
    }

    return L.latLngBounds(allPoints);
  }, [polygonPaths]);

  // Calculate map center
  const mapCenterLatLng = useMemo(() => {
    if (mapBounds && mapBounds.isValid()) {
      const mapCenterValue = mapBounds.getCenter();
      return [mapCenterValue.lat, mapCenterValue.lng];
    }

    return [center.lat, center.lng];
  }, [mapBounds]);

  // Handle active image click for a modal (if using one)
  function handleActiveClick(index) {
    setActiveImage(index);
    console.log("Active image index:", index);
    if (cropSurveyData && cropSurveyData.farmerCropSurveyImageList) {
      const imagePath = cropSurveyData.farmerCropSurveyImageList[index].attPath;
      const innerHTML = `<img src="${imagePath}" alt="Farmer Image" class="img-fluid w-100 h-50" />`;
      document.getElementById("modalBody").innerHTML = innerHTML;
    }
  }

  return (
    <main id="main" className="main">
      <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
        <nav>
          <h1>Farmer Details View</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Farmer</li>
            <li className="breadcrumb-item active">Farmer Details View</li>
          </ol>
        </nav>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => navigate("/newadminpanel/farmer")}
        >
          <i>
            <FontAwesomeIcon icon={faArrowLeft} />
          </i>{" "}
          Back
        </button>
      </div>
      <section className="section">
        <div className="row">
          <div className="col-lg-12 dashboard">
            <div className="card">
              {cropSurveyData && (schemeCodeModel === "1" || !schemeCodeModel) && (
                <div className="card-body p-5 pt-2">
                  {/* Polygon Details with Bhuvan Map */}
                  <div className="row cropview">
                    <h5 className="formgrouping">Polygon Details</h5>
                    <MapContainer
                      center={mapCenterLatLng}
                      zoom={14}
                      maxZoom={18}
                      style={containerStyle}
                      scrollWheelZoom
                      zoomControl={true}
                    >
                      {/* Bhuvan WMS Base Layer */}
                      <BhuvanWMSTileLayer
                        url={bhuvanLayerUrl}
                        layers="bhuvan_wbase_ind"
                      />
                      
                      {/* Polygon Overlays */}
                      {polygonPaths.map((path, index) => (
                        <Polygon
                          key={`farmer-polygon-${index}`}
                          positions={path}
                          pathOptions={{
                            color: "#FF0000",
                            fillColor: "#FF0000",
                            fillOpacity: 0.1,
                            weight: 2,
                          }}
                          eventHandlers={{
                            mouseover: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.3,
                                weight: 3,
                              });
                            },
                            mouseout: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.1,
                                weight: 2,
                              });
                            },
                          }}
                        />
                      ))}
                      
                      {/* Auto-fit bounds */}
                      {mapBounds ? <FitBounds bounds={mapBounds} /> : null}
                    </MapContainer>
                  </div>
                  
                  {/* Survey Details */}
                  <h5 className="formgrouping">Survey Details</h5>
                  <div className="row surveydetailsbg">
                    {cropSurveyData.schemeName && (
                      <div className="col-md-3">
                        <h6>Scheme Name</h6>
                        <p>{cropSurveyData.schemeName}</p>
                      </div>
                    )}
                    {cropSurveyData.surveyUserCode && (
                      <div className="col-md-3">
                        <h6>Survey No.</h6>
                        <p>{cropSurveyData.surveyUserCode}</p>
                      </div>
                    )}
                    {cropSurveyData.totalArea && (
                      <div className="col-md-3">
                        <h6>Total Area </h6>
                        <p>{cropSurveyData.totalArea}</p>
                      </div>
                    )}
                    {cropSurveyData.landArea && (
                      <div className="col-md-3">
                        <h6>land Area</h6>
                        <p>{cropSurveyData.landArea}</p>
                      </div>
                    )}
                    {cropSurveyData.farmerName && (
                      <div className="col-md-3">
                        <h6>Farmer Name</h6>
                        <p>{cropSurveyData.farmerName}</p>
                      </div>
                    )}
                    {cropSurveyData.farmerID && (
                      <div className="col-md-3">
                        <h6>Farmer Id</h6>
                        <p>{cropSurveyData.farmerID}</p>
                      </div>
                    )}
                    {cropSurveyData.stateName && (
                      <div className="col-md-3">
                        <h6>State</h6>
                        <p>{cropSurveyData.stateName}</p>
                      </div>
                    )}
                    {cropSurveyData.districtName && (
                      <div className="col-md-3">
                        <h6>District</h6>
                        <p>{cropSurveyData.districtName}</p>
                      </div>
                    )}
                    {cropSurveyData.subDistrictName && (
                      <div className="col-md-3">
                        <h6>Sub District</h6>
                        <p>{cropSurveyData.subDistrictName}</p>
                      </div>
                    )}
                    {cropSurveyData.villageName && (
                      <div className="col-md-3">
                        <h6>Village</h6>
                        <p>{cropSurveyData.villageName}</p>
                      </div>
                    )}
                    {cropSurveyData.mobileNo && (
                      <div className="col-md-3">
                        <h6>Mobile No</h6>
                        <p>{cropSurveyData.mobileNo}</p>
                      </div>
                    )}
                    {cropSurveyData.createdOn && (
                      <div className="col-md-3">
                        <h6>Created Date</h6>
                        <p>{cropSurveyData.createdOn.split("T")[0]}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Farmer Plot Registration List */}
                  {cropSurveyData.farmerCropDetailsExtsList &&
                    cropSurveyData.farmerCropDetailsExtsList.length !== 0 && (
                      <>
                        <h5 className="formgrouping">Farmer Plot Registration List</h5>
                        <div className="row surveydetailsbg">
                          {cropSurveyData.farmerCropDetailsExtsList.map((item, index) => (
                            <React.Fragment key={index}>
                              {item.cropGroupName && (
                                <div className="col-md-3">
                                  <h6>Crop Group Name </h6>
                                  <p>{item.cropGroupName}</p>
                                </div>
                              )}
                              {item.cropName && (
                                <div className="col-md-3">
                                  <h6>Crop Name</h6>
                                  <p>{item.cropName}</p>
                                </div>
                              )}
                              {item.cropVarietyName && (
                                <div className="col-md-3">
                                  <h6>Crop Variety Name  </h6>
                                  <p>{item.cropVarietyName}</p>
                                </div>
                              )}
                              {item.seasonName && (
                                <div className="col-md-3">
                                  <h6>Season Name</h6>
                                  <p>{item.seasonName}</p>
                                </div>
                              )}
                              {item.numofoilpalmtrees && (
                                <div className="col-md-3">
                                  <h6>Number of Oil Palm Trees</h6>
                                  <p>{item.numofoilpalmtrees}</p>
                                </div>
                              )}
                              {item.surveyNo && (
                                <div className="col-md-3">
                                  <h6>Survey No.</h6>
                                  <p>{item.surveyNo}</p>
                                </div>
                              )}
                              {item.plotCode && (
                                <div className="col-md-3">
                                  <h6>Plot Code</h6>
                                  <p>{item.plotCode}</p>
                                </div>
                              )}
                              {item.plantMonthYear && (
                                <div className="col-md-3">
                                  <h6>Plant Month/Year</h6>
                                  <p>{item.plantMonthYear}</p>
                                </div>
                              )}
                              {item.fbbProduction && (
                                <div className="col-md-3">
                                  <h6>FBB Production</h6>
                                  <p>{item.fbbProduction}</p>
                                </div>
                              )}
                              {item.agencyName && (
                                <div className="col-md-3">
                                  <h6>Agency Name</h6>
                                  <p>{item.agencyName}</p>
                                </div>
                              )}
                              {item.createdOn && (
                                <div className="col-md-3">
                                  <h6>Created On</h6>
                                  <p>{item.createdOn.split("T")[0]}</p>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </>
                    )}
                  
                  {/* Farmer Crop Survey Image List */}
                  {cropSurveyData.farmerCropSurveyImageList &&
                    cropSurveyData.farmerCropSurveyImageList.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Image Details</h5>
                        {cropSurveyData.farmerCropSurveyImageList.map((item, index) => (
                          <React.Fragment key={index}>
                            <div className="col-md-2">
                              <img
                                src={`${
                                  process.env.NODE_ENV === "development"
                                    ? process.env.REACT_APP_DEV_MODE
                                    : process.env.REACT_APP_PRO_MODE
                                }/api/common/GetAttachbyURL?imageURL=/${item.attPath}`}
                                alt="Farmer Image"
                                data-bs-toggle="modal"
                                data-bs-target="#staticBackdrop"
                                className="img-fluid imgdetials"
                                onClick={() => handleActiveClick(index)}
                              />
                            </div>
                            <div className="col-md-4">
                              <div className="row imgdetialsbg">
                                <div className="col-md-6">
                                  <h6>Latitude</h6>
                                  <p>{item.surveyorLat}</p>
                                </div>
                                <div className="col-md-6">
                                  <h6>Longitude</h6>
                                  <p>{item.surveyorLong}</p>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PDMCDeatilPage;